import React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { IconDelete } from '@bynder/icons';
import { useHover } from 'react-laag';
import { useTranslate } from '@bynder/localization';
import { Button } from '@bynder/design-system';
import { WIDGET_DEBOUNCE_WHILE_TYPING } from '../../constants/app';
import Color, {
  CMYK_CODE_LENGTH,
  isValidCMYKCode,
  isValidHexCode,
  isValidRGBCode,
  repairHexCodeInput,
  RGB_CODE_LENGTH,
} from '../../helpers/colors';
import {
  CustomSwatchRow,
  StyledColorSwatch,
  SwatchColorName,
  SwatchRow,
  SwatchTile,
} from '../shared/styled-components/Widgets';

import { ColorSwatchCode } from './ColorSwatchCode';
import { CustomColorSwatchKey, ModeTitle } from './CustomColorSwatchKey';
import { ColorCode, ColorValuesShown } from '../../types';
import { DefaultColorsEnum, WidgetPosition } from '../../constants/widgets';
import { CSS_CLASSES } from '../guides/Inspector/constants';
import { removeDot } from '../guides/Inspector/helpers';

interface Props {
  defaultColorsShown: ColorValuesShown;
  colorName: string;
  hexCode: string;
  position: number;
  widgetPosition: WidgetPosition;
  deleteColor: (position: number) => void;
  editColor: (
    colorName: string,
    colorCodes: ColorCode[],
    position: number,
    rowIndex: number,
  ) => void;
  readOnly: boolean;
  numberOfColumns: number;
  onDeleteRow: (
    position: number,
    rowIndex: number,
    colorCodes: ColorCode[],
  ) => void;
  colorCodes: ColorCode[];
  nameStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
  modeStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
  valueStyles: {
    fontFamily: string;
    fontSize: number;
    fontStyle: string;
    color: string;
  };
  borderRadius: string;
}

const ColorSwatch: React.FC<Props> = props => {
  const { translate } = useTranslate();
  const [colorName, setColorName] = React.useState(props.colorName || '');
  const [tileColor, setTileColor] = React.useState(props.hexCode || '');
  const [hexCode, setHexCode] = React.useState(props.hexCode || '');
  const [rgbCode, setRgbCode] = React.useState('');
  const [cmykCode, setCmykCode] = React.useState('');
  const [colorCodes, setColorCodes] = React.useState<ColorCode[]>(
    props.colorCodes || [],
  );
  const defaultCodesLength = Object.keys(DefaultColorsEnum).length;
  const [isHovering, hoverProps] = useHover({ hideOnScroll: false });

  let color;

  React.useEffect(() => {
    if (isValidHexCode(props.hexCode)) {
      color = new Color(props.hexCode);
      setRgbCode(color.toRGB());
      setCmykCode(color.toCMYK());
    } else {
      color = '';
    }
  }, [props.hexCode]);

  React.useEffect(() => {
    const lastColor = props.colorCodes[props.colorCodes.length - 1];

    if (lastColor.key.length || lastColor.value.length) {
      setColorCodes([...props.colorCodes.concat({ key: '', value: '' })]);
    } else {
      setColorCodes(props.colorCodes);
    }
  }, [props.colorCodes]);

  const debouncedEditColor = useDebouncedCallback(
    props.editColor,
    WIDGET_DEBOUNCE_WHILE_TYPING,
  );

  const onDeleteColor = () => props.deleteColor(props.position);

  const onDeleteRow = (rowIndex: number) =>
    props.onDeleteRow(props.position, rowIndex, colorCodes);

  const onChangeColorName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColorName(e.target.value);

    debouncedEditColor(e.target.value, colorCodes, props.position, 0);
  };

  const setColor = (newColor: Color, type: DefaultColorsEnum) => {
    setTileColor(newColor.toHex());

    setColorCodes(state => {
      state[0].value = newColor.toHex();
      state[1].value = newColor.toRGB();
      state[2].value = newColor.toCMYK();

      return [...state];
    });

    debouncedEditColor(colorName, colorCodes, props.position, 0);

    if (type !== DefaultColorsEnum.HEX) {
      setHexCode(newColor.toHex());
    }

    if (type !== DefaultColorsEnum.RGB) {
      setRgbCode(newColor.toRGB());
    }

    if (type !== DefaultColorsEnum.CMYK) {
      setCmykCode(newColor.toCMYK());
    }
  };

  const resetColor = (type: DefaultColorsEnum, value: string) => {
    setTileColor('');

    if (type !== DefaultColorsEnum.HEX) {
      setHexCode('');
    }

    if (type !== DefaultColorsEnum.RGB) {
      setRgbCode('');
    }

    if (type !== DefaultColorsEnum.CMYK) {
      setCmykCode('');
    }

    setColorCodes(state => {
      const rowIndex = Object.values(DefaultColorsEnum).indexOf(type);

      state[0].value = '';
      state[1].value = '';
      state[2].value = '';

      state[rowIndex].value = value;

      return [...state];
    });

    debouncedEditColor(colorName, colorCodes, props.position, 0);
  };

  const onChangeHexCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const value = repairHexCodeInput(e.target.value.trim());

      setHexCode(value);

      if (isValidHexCode(value)) {
        setColor(new Color(value), DefaultColorsEnum.HEX);
      } else {
        resetColor(DefaultColorsEnum.HEX, value);
      }
    } catch (error) {
      // Ignore input if hex code is too big
    }
  };

  const onChangeRGB = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length > RGB_CODE_LENGTH) {
      return;
    }

    setRgbCode(value);

    if (isValidRGBCode(value)) {
      setColor(Color.fromRGB(value), DefaultColorsEnum.RGB);
    } else {
      resetColor(DefaultColorsEnum.RGB, value);
    }
  };

  const onChangeCMYK = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value.length > CMYK_CODE_LENGTH) {
      return;
    }

    setCmykCode(value);

    if (isValidCMYKCode(value)) {
      setColor(Color.fromCMYK(value), DefaultColorsEnum.CMYK);
    } else {
      resetColor(DefaultColorsEnum.CMYK, value);
    }
  };

  const onChangeCustomKey = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number,
  ) => {
    const value = e.target.value;

    setColorCodes(state => {
      state[rowIndex].key = value;

      return [...state];
    });

    debouncedEditColor(colorName, colorCodes, props.position, rowIndex);
  };

  const onChangeCustomCode = (
    e: React.ChangeEvent<HTMLInputElement>,
    rowIndex: number,
  ) => {
    const value = e.target.value;

    setColorCodes(state => {
      state[rowIndex].value = value;

      return [...state];
    });

    debouncedEditColor(colorName, colorCodes, props.position, rowIndex);
  };

  const isCustomEmpty = (customColor: ColorCode) =>
    !customColor.key?.length || !customColor.value?.length;

  const isCustomEmptyAndReadOnly = (customColor: ColorCode) =>
    isCustomEmpty(customColor) && props.readOnly;

  const customColorCodes = colorCodes.slice(
    Object.values(DefaultColorsEnum).length,
  );

  return (
    <StyledColorSwatch
      $widgetPosition={props.widgetPosition}
      $numberOfColumns={props.numberOfColumns}
    >
      <SwatchTile
        $borderRadius={props.borderRadius}
        style={{ backgroundColor: tileColor }}
        {...hoverProps}
      >
        {!props.readOnly && isHovering && (
          <Button
            title="Delete color"
            variant="secondary"
            icon={<IconDelete />}
            onClick={onDeleteColor}
          />
        )}
      </SwatchTile>
      <>
        <SwatchColorName
          className={removeDot(CSS_CLASSES.COLOR_PALETTE_NAME)}
          value={colorName}
          onChange={onChangeColorName}
          placeholder={
            !props.readOnly
              ? translate('COLOR_SWATCH.COLOR_NAME_PLACEHOLDER')
              : null
          }
          disabled={props.readOnly}
          $nameStyles={props.nameStyles}
        />
        {props.defaultColorsShown.hex && (
          <SwatchRow readOnly={props.readOnly}>
            <ModeTitle
              $modeStyles={props.modeStyles}
              className={removeDot(CSS_CLASSES.COLOR_PALETTE_KEY)}
            >
              HEX
            </ModeTitle>
            <ColorSwatchCode
              $valueStyles={props.valueStyles}
              label="hex"
              code={hexCode}
              onChangeCode={onChangeHexCode}
              readOnly={props.readOnly}
            />
          </SwatchRow>
        )}
        {props.defaultColorsShown.rgb && (
          <SwatchRow readOnly={props.readOnly}>
            <ModeTitle
              $modeStyles={props.modeStyles}
              className={removeDot(CSS_CLASSES.COLOR_PALETTE_KEY)}
            >
              RGB
            </ModeTitle>
            <ColorSwatchCode
              $valueStyles={props.valueStyles}
              label="RGB"
              code={rgbCode}
              onChangeCode={onChangeRGB}
              readOnly={props.readOnly}
            />
          </SwatchRow>
        )}
        {props.defaultColorsShown.cmyk && (
          <SwatchRow readOnly={props.readOnly}>
            <ModeTitle
              $modeStyles={props.modeStyles}
              className={removeDot(CSS_CLASSES.COLOR_PALETTE_KEY)}
            >
              CMYK
            </ModeTitle>
            <ColorSwatchCode
              $valueStyles={props.valueStyles}
              label="CMYK"
              code={cmykCode}
              onChangeCode={onChangeCMYK}
              readOnly={props.readOnly}
            />
          </SwatchRow>
        )}
        {customColorCodes.map((item: ColorCode, index: number) => {
          return (
            !isCustomEmptyAndReadOnly(item) && (
              <CustomSwatchRow readOnly={props.readOnly} key={index}>
                <CustomColorSwatchKey
                  $modeStyles={props.modeStyles}
                  label={item.key}
                  code={item.value}
                  row={index + defaultCodesLength}
                  readOnly={props.readOnly}
                  onChangeCode={onChangeCustomKey}
                  onDeleteRow={onDeleteRow}
                />
                <ColorSwatchCode
                  $valueStyles={props.valueStyles}
                  label={item.key}
                  code={item.value}
                  row={index + defaultCodesLength}
                  readOnly={props.readOnly}
                  onChangeCode={onChangeCustomCode}
                  onDeleteRow={onDeleteRow}
                />
              </CustomSwatchRow>
            )
          );
        })}
      </>
    </StyledColorSwatch>
  );
};

export default ColorSwatch;
